/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback, ReactNode } from "react";
import { createPortal } from "react-dom";
import { ITfidTags, IAllTfidTagsResponse, useTfidTagsService } from "../services/registry/TfidTagsService";
import { TfidTooltip } from "../components/TfidTooltip";
import { TfidTagEditorDialog } from "../components/TfidTagEditorDialog";

const _CSS = CSS as any; // For TS to allow dynamic highlight registration

// Shared regex (must match TfidCodemirrorExtension.ts)
const TFID_REGEX = /\b[0-9a-z]{13}\b/g;

interface TfidHighlightContextValue {
  tfidTagsMap: Map<string, ITfidTags>;
  refreshTfidTags: () => Promise<void>;
}

const TfidHighlightContext = createContext<TfidHighlightContextValue | undefined>(undefined);

export const useTfidHighlight = (): TfidHighlightContextValue | undefined => useContext(TfidHighlightContext);

/**
 * Walk all text nodes under `root`, skipping CodeMirror editors.
 * @param root - The root node to walk.
 * @returns An array of ranges that contain TFIDs.
 */
function collectTfidRanges(root: Node): Range[] {
  const ranges: Range[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      // Skip nodes inside CodeMirror (handled by the CM extension)
      const parent = node.parentElement;
      if (parent?.closest(".cm-editor")) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  let textNode: Text | null;
  while ((textNode = walker.nextNode() as Text | null)) {
    const text = textNode.textContent;
    if (!text) continue;

    TFID_REGEX.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = TFID_REGEX.exec(text)) !== null) {
      try {
        const range = new Range();
        range.setStart(textNode, match.index);
        range.setEnd(textNode, match.index + 13);
        ranges.push(range);
      } catch {
        // Range creation can fail if node was removed between iteration
      }
    }
  }
  return ranges;
}

/**
 * Extract a TFID from the text under the cursor using caretRangeFromPoint.
 * Returns the matched TFID string or null.
 */
function getTfidAtPoint(x: number, y: number): string | null {
  const caretRange = typeof document.caretRangeFromPoint === "function" ? document.caretRangeFromPoint(x, y) : null;

  if (!caretRange) return null;

  const textNode = caretRange.startContainer;
  if (textNode.nodeType !== Node.TEXT_NODE) return null;

  const text = textNode.textContent ?? "";
  const offset = caretRange.startOffset;

  TFID_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TFID_REGEX.exec(text)) !== null) {
    if (offset >= match.index && offset <= match.index + 13) {
      return match[0];
    }
  }
  return null;
}

interface TfidHighlightProviderProps {
  children: ReactNode;
}

export const TfidHighlightProvider = ({ children }: TfidHighlightProviderProps) => {
  const tfidTagsService = useTfidTagsService();
  const [tfidTagsMap, setTfidTagsMap] = useState<Map<string, ITfidTags>>(new Map());

  // Tooltip state
  const [tooltipTfid, setTooltipTfid] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  // Editor dialog state
  const [editorVisible, setEditorVisible] = useState(false);
  const [editorTfid, setEditorTfid] = useState<string>("");

  // Refs for non-reactive state used in event handlers
  const rafId = useRef<number>(0);
  const scanRafId = useRef<number>(0);
  const tooltipTfidRef = useRef<string | null>(null);
  const isMouseOverTooltipRef = useRef(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refreshTfidTags = useCallback(async () => {
    try {
      const response: IAllTfidTagsResponse = await tfidTagsService.getAllTfidTags();
      if (response?.content) {
        const map = new Map<string, ITfidTags>();
        for (const [id, tags] of Object.entries(response.content)) {
          map.set(id, tags);
        }
        setTfidTagsMap(map);
      }
    } catch {
      // Silently ignore — tags are a non-critical enhancement
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refreshTfidTags();
  }, [refreshTfidTags]);

  const cancelHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current !== null) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    cancelHideTimeout();
    hideTimeoutRef.current = setTimeout(() => {
      if (!isMouseOverTooltipRef.current) {
        tooltipTfidRef.current = null;
        setTooltipTfid(null);
      }
      hideTimeoutRef.current = null;
    }, 150);
  }, [cancelHideTimeout]);

  const handleTooltipMouseEnter = useCallback(() => {
    isMouseOverTooltipRef.current = true;
    cancelHideTimeout();
  }, [cancelHideTimeout]);

  const handleTooltipMouseLeave = useCallback(() => {
    isMouseOverTooltipRef.current = false;
    scheduleHide();
  }, [scheduleHide]);

  const performScan = useCallback(() => {
    if (!("Highlight" in window) || !_CSS.highlights) return;

    const ranges = collectTfidRanges(document.body);
    if (ranges.length > 0) {
      const highlight = new (window as any).Highlight(...ranges);
      _CSS.highlights.set("tfid-highlight", highlight);
    } else {
      _CSS.highlights.delete("tfid-highlight");
    }
  }, []);

  const scheduleScan = useCallback(() => {
    cancelAnimationFrame(scanRafId.current);
    scanRafId.current = requestAnimationFrame(performScan);
  }, [performScan]);

  useEffect(() => {
    // Initial scan
    scheduleScan();

    // Observe DOM mutations
    const observer = new MutationObserver(() => {
      scheduleScan();
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });

    return () => {
      observer.disconnect();
      cancelAnimationFrame(scanRafId.current);
      if (_CSS.highlights) {
        _CSS.highlights.delete("tfid-highlight");
      }
    };
  }, [scheduleScan]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => {
        // Don't change tooltip while the mouse is over it
        if (isMouseOverTooltipRef.current) return;

        const tfid = getTfidAtPoint(e.clientX, e.clientY);
        if (tfid) {
          cancelHideTimeout();
          if (tfid !== tooltipTfidRef.current) {
            tooltipTfidRef.current = tfid;
            setTooltipTfid(tfid);
            setTooltipPos({ x: e.clientX, y: e.clientY });
          }
        } else {
          if (tooltipTfidRef.current !== null) {
            scheduleHide();
          }
        }
      });
    };

    document.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      cancelAnimationFrame(rafId.current);
    };
  }, [cancelHideTimeout, scheduleHide]);

  // Hide tooltip on scroll / click outside
  useEffect(() => {
    const hide = () => {
      cancelHideTimeout();
      isMouseOverTooltipRef.current = false;
      tooltipTfidRef.current = null;
      setTooltipTfid(null);
    };
    // Use capture so we catch scroll on inner containers
    document.addEventListener("scroll", hide, { capture: true, passive: true });
    document.addEventListener("mousedown", hide, { passive: true });
    return () => {
      document.removeEventListener("scroll", hide, { capture: true } as EventListenerOptions);
      document.removeEventListener("mousedown", hide);
      cancelHideTimeout();
    };
  }, [cancelHideTimeout]);

  const handleTooltipEdit = useCallback(() => {
    if (tooltipTfid) {
      cancelHideTimeout();
      isMouseOverTooltipRef.current = false;
      setEditorTfid(tooltipTfid);
      setEditorVisible(true);
      setTooltipTfid(null);
      tooltipTfidRef.current = null;
    }
  }, [tooltipTfid, cancelHideTimeout]);

  const handleEditorSave = useCallback(
    async (tags: string[], label?: string, env?: string) => {
      await tfidTagsService.upsertTfidTag(editorTfid, tags, label, env);
      setEditorVisible(false);
      await refreshTfidTags();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editorTfid, refreshTfidTags],
  );

  const handleEditorCancel = useCallback(() => {
    setEditorVisible(false);
  }, []);

  const handleTooltipClose = useCallback(() => {
    cancelHideTimeout();
    isMouseOverTooltipRef.current = false;
    tooltipTfidRef.current = null;
    setTooltipTfid(null);
  }, [cancelHideTimeout]);

  const contextValue = React.useMemo<TfidHighlightContextValue>(
    () => ({ tfidTagsMap, refreshTfidTags }),
    [tfidTagsMap, refreshTfidTags],
  );

  return (
    <TfidHighlightContext.Provider value={contextValue}>
      {/* Inject CSS for the Highlight API */}
      <style>{`
        ::highlight(tfid-highlight) {
          background-color: rgba(255, 213, 79, 0.30);
          text-decoration: underline dotted #f57c00 1.5px;
        }
      `}</style>

      {children}

      {/* Tooltip portal */}
      {tooltipTfid &&
        createPortal(
          <TfidTooltip
            tfid={tooltipTfid}
            anchorPosition={tooltipPos}
            tagsInfo={tfidTagsMap.get(tooltipTfid)}
            onEdit={handleTooltipEdit}
            onClose={handleTooltipClose}
            onMouseEnter={handleTooltipMouseEnter}
            onMouseLeave={handleTooltipMouseLeave}
          />,
          document.body,
        )}

      {/* Editor dialog */}
      <TfidTagEditorDialog
        visible={editorVisible}
        tfid={editorTfid}
        initialData={tfidTagsMap.get(editorTfid)}
        onSave={handleEditorSave}
        onCancel={handleEditorCancel}
      />
    </TfidHighlightContext.Provider>
  );
};
